import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControl,
  FormGroup,
  FormLabel,
  Divider,
  Stack,
} from '@mui/material';
import {
  SearchOutlined,
  AddBusinessOutlined,
  GroupOutlined,
  PersonAddAlt1Outlined,
  PhoneOutlined,
  EmailOutlined,
  KeyOutlined,
  EditOutlined,
  StorefrontOutlined,
  LocationOnOutlined,
  AddLocationAltOutlined,
  DescriptionOutlined,
  ReceiptLongOutlined,
  CalendarMonthOutlined,
  PercentOutlined,
  PaymentsOutlined,
  AddCircleOutline,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import { restaurantsService, companyService } from '../api/services';
import { useLanguage } from '../context/LanguageContext';
import { getApiErrorMessage } from '../utils/errorHandler';

const Restaurants = () => {
  const { t, isRtl, dir } = useLanguage();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [formErrors, setFormErrors] = useState({});

  // Button Loading States
  const [submittingRestaurant, setSubmittingRestaurant] = useState(false);
  const [submittingUser, setSubmittingUser] = useState(false);
  const [submittingPwd, setSubmittingPwd] = useState(false);
  const [submittingEditUser, setSubmittingEditUser] = useState(false);

  // Dynamic Restaurant Roles
  const [restaurantRoles, setRestaurantRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Restaurant Branches Modal State
  const [branchesModalOpen, setBranchesModalOpen] = useState(false);
  const [selectedRestaurantForBranches, setSelectedRestaurantForBranches] = useState(null);
  const [restaurantBranches, setRestaurantBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [submittingBranch, setSubmittingBranch] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchForm, setBranchForm] = useState({
    branchCode: '',
    branchNameAr: '',
    branchNameEn: '',
    latitude: 30.0444,
    longitude: 31.2357,
    addressText: '',
    phone: '',
    maximumBatchOrders: 3,
    isBatchingEnabled: true,
    isActive: true,
  });

  // Restaurant Users Modal State
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurantUsers, setRestaurantUsers] = useState([]);
  const [userBranches, setUserBranches] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    restaurantBranchId: '',
    userName: '',
    fullName: '',
    email: '',
    mobile: '',
    isActive: true,
    roleId: '',
    password: '',
    rowVersion: '',
  });

  // Password Reset Dialog State
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [selectedUserForPwd, setSelectedUserForPwd] = useState(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');

  // Edit User Dialog State
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editUserForm, setEditUserForm] = useState({
    id: null,
    restaurantBranchId: '',
    userName: '',
    fullName: '',
    email: '',
    phone: '',
    role: 'Manager',
    rowVersion: '',
  });

  // Restaurant Contracts Modal State
  const [contractsModalOpen, setContractsModalOpen] = useState(false);
  const [selectedRestaurantForContracts, setSelectedRestaurantForContracts] = useState(null);
  const [restaurantContracts, setRestaurantContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [submittingContract, setSubmittingContract] = useState(false);
  const [contractBranches, setContractBranches] = useState([]);
  const [contractForm, setContractForm] = useState({
    contractNumber: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    courierPaymentDisplayMode: 0,
    additionalOrderFeePercentage: 0,
    allowZeroDeliveryFee: true,
    statementCycle: 1,
    paymentTermDays: 7,
    status: 2,
    branchIds: [],
  });

  // Edit Contract Dialog State
  const [editContractOpen, setEditContractOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [submittingEditContract, setSubmittingEditContract] = useState(false);
  const [editContractForm, setEditContractForm] = useState({
    contractNumber: '',
    effectiveFrom: '',
    effectiveTo: '',
    courierPaymentDisplayMode: 0,
    additionalOrderFeePercentage: 0,
    allowZeroDeliveryFee: true,
    statementCycle: 1,
    paymentTermDays: 7,
    status: 2, // 1 = disabled (switch OFF), 2 = active (switch ON)
    branchIds: [],
    rowVersion: '',
  });

  // Add Restaurant Form based on Swagger Schema
  const [newRestaurant, setNewRestaurant] = useState({
    restaurantCode: '',
    restaurantNameAr: '',
    restaurantNameEn: '',
    legalName: '',
    taxRegistrationNumber: '',
    contactName: '',
    phone: '',
    email: '',
    isActive: true,
  });

  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      const res = await restaurantsService.getRestaurants();
      const rawList = Array.isArray(res) ? res : (res?.items || res?.data || []);
      const normalized = rawList.map((r) => ({
        ...r,
        id: r.id || r.restaurantId || r.Id || r.RestaurantId,
      }));
      setRestaurants(normalized);
    } catch (e) {
      console.warn('Restaurants fetch error:', e);
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantRoles = async () => {
    setRolesLoading(true);
    try {
      const data = await companyService.getRestaurantRoles();
      const rawRoles = Array.isArray(data) ? data : (data?.items || data?.data || []);
      const validRoles = rawRoles.map((r) => ({
        roleId: r.roleId ?? r.id ?? r.RoleId ?? r.Id,
        roleCode: r.roleCode || r.code || '',
        roleNameAr: r.roleNameAr || r.roleName || r.nameAr || r.name || '',
        roleNameEn: r.roleNameEn || r.roleName || r.nameEn || r.name || '',
        isSystem: r.isSystem,
        isActive: r.isActive,
      }));
      setRestaurantRoles(validRoles);
      if (validRoles.length > 0) {
        setNewUserForm((prev) => ({
          ...prev,
          roleId: prev.roleId || validRoles[0].roleId,
        }));
      }
    } catch (err) {
      console.warn('Failed to fetch restaurant roles:', err);
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
    fetchRestaurantRoles();
  }, []);

  const filteredRestaurants = restaurants.filter((r) => {
    const nameAr = (r.restaurantNameAr || r.name || r.restaurantName || '').toLowerCase();
    const nameEn = (r.restaurantNameEn || '').toLowerCase();
    const code = (r.restaurantCode || r.code || '').toLowerCase();
    const phone = r.phone || '';
    const query = searchTerm.toLowerCase();
    return nameAr.includes(query) || nameEn.includes(query) || code.includes(query) || phone.includes(query);
  });

  const handleAddRestaurant = async (e) => {
    if (e) e.preventDefault();

    const errors = {};
    if (!newRestaurant.restaurantNameAr.trim()) {
      errors.restaurantNameAr = t('requiredField');
    }
    if (!newRestaurant.phone.trim()) {
      errors.phone = t('requiredField');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setToast({ open: true, message: isRtl ? 'يرجى إدخال اسم المطعم ورقم الهاتف' : 'Please enter restaurant name and phone', severity: 'error' });
      return;
    }

    setFormErrors({});

    const payload = {
      restaurantCode: newRestaurant.restaurantCode.trim() || `REST-${Date.now().toString().slice(-4)}`,
      restaurantNameAr: newRestaurant.restaurantNameAr.trim(),
      restaurantNameEn: newRestaurant.restaurantNameEn.trim() || newRestaurant.restaurantNameAr.trim(),
      legalName: newRestaurant.legalName.trim(),
      taxRegistrationNumber: newRestaurant.taxRegistrationNumber.trim(),
      contactName: newRestaurant.contactName.trim(),
      phone: newRestaurant.phone.trim(),
      email: newRestaurant.email.trim(),
      isActive: newRestaurant.isActive,
    };

    setSubmittingRestaurant(true);
    try {
      await restaurantsService.createRestaurant(payload);
      setToast({ open: true, message: `${isRtl ? 'تمت إضافة المطعم بنجاح!' : 'Restaurant added successfully!'} (${payload.restaurantNameAr})`, severity: 'success' });
      setAddModalOpen(false);
      setNewRestaurant({
        restaurantCode: '',
        restaurantNameAr: '',
        restaurantNameEn: '',
        legalName: '',
        taxRegistrationNumber: '',
        contactName: '',
        phone: '',
        email: '',
        isActive: true,
      });
      fetchRestaurants();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل إضافة المطعم' : 'Failed to add restaurant'), severity: 'error' });
    } finally {
      setSubmittingRestaurant(false);
    }
  };

  // ==========================================
  // Restaurant Branches Actions
  // ==========================================
  const handleOpenBranchesModal = async (restaurant) => {
    const restId = restaurant?.id || restaurant?.restaurantId || restaurant?.Id || restaurant?.RestaurantId;
    if (!restId) return;

    setSelectedRestaurantForBranches({ ...restaurant, id: restId });
    setBranchesModalOpen(true);
    setEditingBranch(null);
    setBranchForm({
      branchCode: `BR-${Math.floor(100 + Math.random() * 900)}`,
      branchNameAr: '',
      branchNameEn: '',
      latitude: 30.0444,
      longitude: 31.2357,
      addressText: '',
      phone: restaurant.phone || '',
      maximumBatchOrders: 3,
      isBatchingEnabled: true,
      isActive: true,
    });
    setBranchesLoading(true);
    try {
      const data = await restaurantsService.getRestaurantBranches(restId);
      const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
      setRestaurantBranches(list);
    } catch (err) {
      console.warn('Failed to load branches:', err);
      setRestaurantBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  };

  const handleEditBranch = (branch) => {
    setEditingBranch(branch);
    setBranchForm({
      branchCode: branch.branchCode || '',
      branchNameAr: branch.branchNameAr || '',
      branchNameEn: branch.branchNameEn || '',
      latitude: branch.latitude || 30.0444,
      longitude: branch.longitude || 31.2357,
      addressText: branch.addressText || '',
      phone: branch.phone || '',
      maximumBatchOrders: branch.maximumBatchOrders || 3,
      isBatchingEnabled: branch.isBatchingEnabled !== undefined ? branch.isBatchingEnabled : true,
      isActive: branch.isActive !== undefined ? branch.isActive : true,
      rowVersion: branch.rowVersion,
    });
  };

  const handleSaveBranch = async (e) => {
    if (e) e.preventDefault();
    const restId = selectedRestaurantForBranches?.id || selectedRestaurantForBranches?.restaurantId;
    if (!restId || !branchForm.branchNameAr.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال اسم الفرع' : 'Please enter branch name', severity: 'error' });
      return;
    }

    setSubmittingBranch(true);
    try {
      if (editingBranch) {
        const branchId = editingBranch.restaurantBranchId || editingBranch.id;
        await restaurantsService.updateRestaurantBranch(branchId, {
          ...branchForm,
          restaurantId: restId,
        });
        setToast({ open: true, message: isRtl ? 'تم تحديث بيانات الفرع بنجاح!' : 'Branch updated successfully!', severity: 'success' });
      } else {
        await restaurantsService.createRestaurantBranch(restId, {
          ...branchForm,
          restaurantId: restId,
        });
        setToast({ open: true, message: isRtl ? 'تمت إضافة الفرع بنجاح!' : 'Branch added successfully!', severity: 'success' });
      }
      setEditingBranch(null);
      setBranchForm({
        branchCode: `BR-${Math.floor(100 + Math.random() * 900)}`,
        branchNameAr: '',
        branchNameEn: '',
        latitude: 30.0444,
        longitude: 31.2357,
        addressText: '',
        phone: '',
        maximumBatchOrders: 3,
        isBatchingEnabled: true,
        isActive: true,
      });
      const data = await restaurantsService.getRestaurantBranches(restId);
      const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
      setRestaurantBranches(list);
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل حفظ الفرع' : 'Failed to save branch'), severity: 'error' });
    } finally {
      setSubmittingBranch(false);
    }
  };

  // ==========================================
  // Restaurant Users Actions
  // ==========================================
  const handleOpenUsersModal = async (restaurant) => {
    const restId = restaurant?.id || restaurant?.restaurantId || restaurant?.Id || restaurant?.RestaurantId;
    if (!restId) {
      setToast({ open: true, message: isRtl ? 'معرف المطعم غير صالح' : 'Invalid Restaurant ID', severity: 'error' });
      return;
    }

    const normalizedRest = { ...restaurant, id: restId };
    setSelectedRestaurant(normalizedRest);
    setUsersModalOpen(true);
    setUsersLoading(true);

    if (restaurantRoles.length === 0) {
      fetchRestaurantRoles();
    }

    try {
      const [usersRes, branchesRes] = await Promise.allSettled([
        restaurantsService.getRestaurantUsers(restId),
        restaurantsService.getRestaurantBranches(restId),
      ]);

      if (usersRes.status === 'fulfilled') {
        const rawUsers = Array.isArray(usersRes.value) ? usersRes.value : (usersRes.value?.items || usersRes.value?.data || []);
        const normUsers = rawUsers.map((u) => ({
          ...u,
          id: u.id || u.userId || u.Id || u.UserId,
        }));
        setRestaurantUsers(normUsers);
      } else {
        setRestaurantUsers([]);
      }

      if (branchesRes.status === 'fulfilled') {
        const rawBranches = Array.isArray(branchesRes.value) ? branchesRes.value : (branchesRes.value?.items || branchesRes.value?.data || []);
        setUserBranches(rawBranches);
      } else {
        setUserBranches([]);
      }
    } catch (e) {
      setRestaurantUsers([]);
      setUserBranches([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddRestaurantUser = async (e) => {
    if (e) e.preventDefault();
    const restId = selectedRestaurant?.id || selectedRestaurant?.restaurantId || selectedRestaurant?.Id;
    if (!restId || !newUserForm.userName.trim() || !newUserForm.password.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال اسم المستخدم وكلمة المرور' : 'Please enter username and password', severity: 'error' });
      return;
    }

    const defaultRoleId = restaurantRoles.length > 0 ? restaurantRoles[0].roleId : 1;
    const finalRoleId = Number(newUserForm.roleId) || Number(defaultRoleId);

    const payload = {
      restaurantBranchId: newUserForm.restaurantBranchId !== '' && newUserForm.restaurantBranchId !== undefined ? Number(newUserForm.restaurantBranchId) : 0,
      userName: newUserForm.userName.trim(),
      fullName: newUserForm.fullName.trim() || newUserForm.userName.trim(),
      email: newUserForm.email.trim(),
      mobile: newUserForm.mobile.trim(),
      isActive: newUserForm.isActive !== undefined ? newUserForm.isActive : true,
      roleIds: [finalRoleId],
      password: newUserForm.password,
      rowVersion: newUserForm.rowVersion || '',
    };

    setSubmittingUser(true);
    try {
      await restaurantsService.createRestaurantUser(restId, payload);
      setToast({ open: true, message: isRtl ? 'تمت إضافة مستخدم المطعم بنجاح!' : 'Restaurant user created successfully!', severity: 'success' });
      setNewUserForm({
        restaurantBranchId: '',
        userName: '',
        fullName: '',
        email: '',
        mobile: '',
        isActive: true,
        roleId: restaurantRoles.length > 0 ? restaurantRoles[0].roleId : '',
        password: '',
        rowVersion: '',
      });
      const users = await restaurantsService.getRestaurantUsers(restId);
      const rawUsers = Array.isArray(users) ? users : (users?.items || users?.data || []);
      setRestaurantUsers(rawUsers.map((u) => ({ ...u, id: u.id || u.userId || u.Id || u.UserId })));
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل إضافة المستخدم' : 'Failed to add user'), severity: 'error' });
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    const restId = selectedRestaurant?.id || selectedRestaurant?.restaurantId || selectedRestaurant?.Id;
    const userId = user?.id || user?.userId || user?.Id || user?.UserId;
    if (!restId || !userId) return;
    try {
      const newStatus = !(user.isActive ?? true);
      await restaurantsService.updateRestaurantUserStatus(restId, userId, newStatus);
      setToast({ open: true, message: `${isRtl ? 'تم تغيير حالة المستخدم إلى:' : 'User status updated to:'} ${newStatus ? t('active') : t('inactive')}`, severity: 'success' });
      const users = await restaurantsService.getRestaurantUsers(restId);
      const rawUsers = Array.isArray(users) ? users : (users?.items || users?.data || []);
      setRestaurantUsers(rawUsers.map((u) => ({ ...u, id: u.id || u.userId || u.Id || u.UserId })));
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل تحديث الحالة' : 'Failed to update status'), severity: 'error' });
    }
  };

  const handleOpenResetPassword = (user) => {
    setSelectedUserForPwd(user);
    setNewPasswordValue('');
    setResetPwdOpen(true);
  };

  const handleSaveResetPassword = async () => {
    const restId = selectedRestaurant?.id || selectedRestaurant?.restaurantId || selectedRestaurant?.Id;
    const userId = selectedUserForPwd?.id || selectedUserForPwd?.userId || selectedUserForPwd?.Id;
    if (!restId || !userId || !newPasswordValue.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال كلمة المرور الجديدة' : 'Please enter new password', severity: 'error' });
      return;
    }

    setSubmittingPwd(true);
    try {
      await restaurantsService.updateRestaurantUserPassword(restId, userId, newPasswordValue);
      setToast({ open: true, message: isRtl ? 'تم إعادة تعيين كلمة المرور بنجاح!' : 'Password reset successfully!', severity: 'success' });
      setResetPwdOpen(false);
      setSelectedUserForPwd(null);
      setNewPasswordValue('');
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل إعادة تعيين كلمة المرور' : 'Failed to reset password'), severity: 'error' });
    } finally {
      setSubmittingPwd(false);
    }
  };

  const handleOpenEditUser = (user) => {
    const userId = user?.id || user?.userId || user?.Id || user?.UserId;
    setEditUserForm({
      id: userId,
      restaurantBranchId: user.restaurantBranchId ?? '',
      userName: user.userName || '',
      fullName: user.fullName || user.displayName || '',
      email: user.email || '',
      phone: user.phone || user.mobile || '',
      role: user.role || 'Manager',
      rowVersion: user.rowVersion || '',
    });
    setEditUserOpen(true);
  };

  const handleSaveEditUser = async () => {
    const restId = selectedRestaurant?.id || selectedRestaurant?.restaurantId || selectedRestaurant?.Id;
    if (!restId || !editUserForm.id) return;

    setSubmittingEditUser(true);
    try {
      await restaurantsService.updateRestaurantUser(restId, editUserForm.id, {
        restaurantBranchId: editUserForm.restaurantBranchId !== '' && editUserForm.restaurantBranchId !== undefined ? Number(editUserForm.restaurantBranchId) : 0,
        userName: editUserForm.userName,
        fullName: editUserForm.fullName.trim(),
        email: editUserForm.email.trim(),
        phone: editUserForm.phone.trim(),
        mobile: editUserForm.phone.trim(),
        role: editUserForm.role,
        rowVersion: editUserForm.rowVersion || '',
      });
      setToast({ open: true, message: isRtl ? 'تم تحديث بيانات المستخدم بنجاح!' : 'User updated successfully!', severity: 'success' });
      setEditUserOpen(false);
      const users = await restaurantsService.getRestaurantUsers(restId);
      const rawUsers = Array.isArray(users) ? users : (users?.items || users?.data || []);
      setRestaurantUsers(rawUsers.map((u) => ({ ...u, id: u.id || u.userId || u.Id || u.UserId })));
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل تحديث بيانات المستخدم' : 'Failed to update user'), severity: 'error' });
    } finally {
      setSubmittingEditUser(false);
    }
  };

  // ==========================================
  // Restaurant Contracts Actions (POST & PUT /api/v1/restaurants/contracts)
  // ==========================================
  const handleOpenContractsModal = async (restaurant) => {
    const restId = restaurant?.id || restaurant?.restaurantId || restaurant?.Id || restaurant?.RestaurantId;
    if (!restId) {
      setToast({ open: true, message: isRtl ? 'معرف المطعم غير صالح' : 'Invalid Restaurant ID', severity: 'error' });
      return;
    }

    const normalizedRest = { ...restaurant, id: restId };
    setSelectedRestaurantForContracts(normalizedRest);
    setContractsModalOpen(true);
    setContractsLoading(true);

    const generatedCode = `CNT-${restaurant.restaurantCode || restId}-${new Date().getFullYear()}-${Math.floor(100 + Math.random() * 900)}`;
    setContractForm({
      contractNumber: generatedCode,
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      courierPaymentDisplayMode: 0,
      additionalOrderFeePercentage: 0,
      allowZeroDeliveryFee: true,
      statementCycle: 1,
      paymentTermDays: 7,
      status: 2, // 2 = Active / Enabled, 1 = Disabled / Inactive
      branchIds: [],
    });

    try {
      const [contractsRes, branchesRes] = await Promise.allSettled([
        restaurantsService.getContracts(restId),
        restaurantsService.getRestaurantBranches(restId),
      ]);

      if (contractsRes.status === 'fulfilled') {
        const raw = contractsRes.value;
        const list = Array.isArray(raw) ? raw : (raw?.items || raw?.data || []);
        setRestaurantContracts(list);
      } else {
        setRestaurantContracts([]);
      }

      if (branchesRes.status === 'fulfilled') {
        const rawB = branchesRes.value;
        const bList = Array.isArray(rawB) ? rawB : (rawB?.items || rawB?.data || []);
        setContractBranches(bList);
      } else {
        setContractBranches([]);
      }
    } catch (e) {
      console.warn('Error loading contracts data:', e);
      setRestaurantContracts([]);
      setContractBranches([]);
    } finally {
      setContractsLoading(false);
    }
  };

  const handleSaveContract = async (e) => {
    if (e) e.preventDefault();
    const restId = selectedRestaurantForContracts?.id || selectedRestaurantForContracts?.restaurantId;
    if (!restId || !contractForm.contractNumber.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال رقم العقد' : 'Please enter contract number', severity: 'error' });
      return;
    }

    setSubmittingContract(true);
    try {
      const payload = {
        restaurantId: Number(restId),
        contractNumber: contractForm.contractNumber.trim(),
        effectiveFrom: contractForm.effectiveFrom,
        effectiveTo: contractForm.effectiveTo,
        courierPaymentDisplayMode: Number(contractForm.courierPaymentDisplayMode),
        additionalOrderFeePercentage: parseFloat(contractForm.additionalOrderFeePercentage) || 0,
        allowZeroDeliveryFee: Boolean(contractForm.allowZeroDeliveryFee),
        statementCycle: Number(contractForm.statementCycle),
        paymentTermDays: Number(contractForm.paymentTermDays) || 0,
        status: Number(contractForm.status), // 2 for active/enabled, 1 for disabled
        branchIds: (contractForm.branchIds || []).map(Number),
      };

      await restaurantsService.createContract(payload);
      setToast({
        open: true,
        message: isRtl ? `تم توثيق وإنشاء العقد (${payload.contractNumber}) بنجاح!` : `Contract created successfully! (${payload.contractNumber})`,
        severity: 'success',
      });

      // Refresh contracts list
      const updated = await restaurantsService.getContracts(restId);
      const list = Array.isArray(updated) ? updated : (updated?.items || updated?.data || []);
      setRestaurantContracts(list);

      // Reset form with a new generated code
      setContractForm({
        contractNumber: `CNT-${selectedRestaurantForContracts?.restaurantCode || restId}-${new Date().getFullYear()}-${Math.floor(100 + Math.random() * 900)}`,
        effectiveFrom: new Date().toISOString().split('T')[0],
        effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        courierPaymentDisplayMode: 0,
        additionalOrderFeePercentage: 0,
        allowZeroDeliveryFee: true,
        statementCycle: 1,
        paymentTermDays: 7,
        status: 2,
        branchIds: [],
      });
    } catch (err) {
      setToast({
        open: true,
        message: getApiErrorMessage(err, isRtl ? 'فشل إنشاء العقد' : 'Failed to create contract'),
        severity: 'error',
      });
    } finally {
      setSubmittingContract(false);
    }
  };

  const handleOpenEditContract = (contract) => {
    setEditingContract(contract);

    // Extract branch IDs safely
    let branchIds = [];
    if (Array.isArray(contract.branchIds)) {
      branchIds = contract.branchIds.map(Number);
    } else if (Array.isArray(contract.branches)) {
      branchIds = contract.branches.map((b) => Number(typeof b === 'object' ? (b.restaurantBranchId || b.id || b.branchId) : b));
    }

    // Status: if 2 -> switch is enabled (2). If 1 or other -> switch is disabled (1)
    const initialStatus = Number(contract.status) === 2 || contract.status === 'Active' ? 2 : 1;

    setEditContractForm({
      contractNumber: contract.contractNumber || '',
      effectiveFrom: contract.effectiveFrom ? contract.effectiveFrom.split('T')[0] : '',
      effectiveTo: contract.effectiveTo ? contract.effectiveTo.split('T')[0] : '',
      courierPaymentDisplayMode: contract.courierPaymentDisplayMode !== undefined ? Number(contract.courierPaymentDisplayMode) : 0,
      additionalOrderFeePercentage: contract.additionalOrderFeePercentage !== undefined ? contract.additionalOrderFeePercentage : 0,
      allowZeroDeliveryFee: contract.allowZeroDeliveryFee !== undefined ? Boolean(contract.allowZeroDeliveryFee) : true,
      statementCycle: contract.statementCycle !== undefined ? Number(contract.statementCycle) : 1,
      paymentTermDays: contract.paymentTermDays !== undefined ? Number(contract.paymentTermDays) : 7,
      status: initialStatus,
      branchIds: branchIds,
      rowVersion: contract.rowVersion || contract.RowVersion || '',
    });
    setEditContractOpen(true);
  };

  const handleSaveEditContract = async (e) => {
    if (e) e.preventDefault();
    const restId = selectedRestaurantForContracts?.id || selectedRestaurantForContracts?.restaurantId;
    const contractId = editingContract?.restaurantContractId || editingContract?.id || editingContract?.contractId || editingContract?.Id;

    if (!restId || !contractId) {
      setToast({ open: true, message: isRtl ? 'معرف العقد غير صالح' : 'Invalid Contract ID', severity: 'error' });
      return;
    }

    if (!editContractForm.contractNumber?.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال رقم العقد' : 'Please enter contract number', severity: 'error' });
      return;
    }

    setSubmittingEditContract(true);
    try {
      const payload = {
        restaurantId: Number(restId),
        contractNumber: editContractForm.contractNumber.trim(),
        effectiveFrom: editContractForm.effectiveFrom,
        effectiveTo: editContractForm.effectiveTo,
        courierPaymentDisplayMode: Number(editContractForm.courierPaymentDisplayMode) || 0,
        additionalOrderFeePercentage: parseFloat(editContractForm.additionalOrderFeePercentage) || 0,
        allowZeroDeliveryFee: Boolean(editContractForm.allowZeroDeliveryFee),
        statementCycle: Number(editContractForm.statementCycle) || 0,
        paymentTermDays: Number(editContractForm.paymentTermDays) || 0,
        status: Number(editContractForm.status), // 1 = disabled switch, 2 = enabled switch
        branchIds: (editContractForm.branchIds || []).map(Number),
        rowVersion: editContractForm.rowVersion || '',
      };

      await restaurantsService.updateContract(contractId, payload);
      setToast({
        open: true,
        message: isRtl ? `تم تحديث وتعديل العقد (${payload.contractNumber}) بنجاح!` : `Contract (${payload.contractNumber}) updated successfully!`,
        severity: 'success',
      });
      setEditContractOpen(false);
      setEditingContract(null);

      // Refresh contracts list
      const updated = await restaurantsService.getContracts(restId);
      const list = Array.isArray(updated) ? updated : (updated?.items || updated?.data || []);
      setRestaurantContracts(list);
    } catch (err) {
      setToast({
        open: true,
        message: getApiErrorMessage(err, isRtl ? 'فشل تعديل العقد' : 'Failed to update contract'),
        severity: 'error',
      });
    } finally {
      setSubmittingEditContract(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.2 }}>
            {t('restaurantsPage.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('restaurantsPage.subtitle')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddBusinessOutlined sx={{ fontSize: 16 }} />}
          onClick={() => {
            setFormErrors({});
            setAddModalOpen(true);
          }}
          sx={{
            borderRadius: '8px',
            fontSize: '0.8rem',
            py: 0.6,
            background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
          }}
        >
          {t('restaurantsPage.addRestaurantBtn')}
        </Button>
      </Box>

      {/* Search & Stats */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} alignItems="center" dir={dir} style={{ direction: dir }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder={isRtl ? 'ابحث باسم المطعم، الكود، أو الهاتف...' : 'Search by restaurant name, code, or phone...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Chip
                label={`${isRtl ? 'إجمالي المطاعم والشركاء' : 'Total Restaurants'}: ${restaurants.length}`}
                sx={{ fontWeight: 700, height: 38, width: '100%', fontSize: '0.8rem', bgcolor: 'action.hover' }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Restaurants Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <LoadingState />
          ) : filteredRestaurants.length === 0 ? (
            <EmptyState message={t('noData')} subtitle={isRtl ? 'أضف أول مطعم وشريك في النظام' : 'Add the first partner restaurant'} />
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: 920 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 230 }}>{isRtl ? 'المطعم' : 'Restaurant'}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{isRtl ? 'المسؤول' : 'Contact Person'}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{t('phone')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{isRtl ? 'الرقم الضريبي' : 'Tax ID'}</TableCell>
                    <TableCell align="center" sx={{ textAlign: 'center !important', width: 100 }}>{t('status')}</TableCell>
                    <TableCell align="center" sx={{ textAlign: 'center !important', minWidth: 260 }}>{isRtl ? 'إدارة وإجراءات المطعم' : 'Actions & Operations'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRestaurants.map((restaurant) => (
                    <TableRow key={restaurant.id} hover>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'secondary.light', color: '#FFFFFF', fontWeight: 700, width: 32, height: 32, fontSize: '0.75rem' }}>
                            {(restaurant.restaurantNameAr || restaurant.name || 'R').charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                              {restaurant.restaurantNameAr || restaurant.name || restaurant.restaurantName}
                              {restaurant.restaurantCode && (
                                <Chip label={restaurant.restaurantCode} size="small" sx={{ ml: 0.5, mr: 0.5, height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                              )}
                            </Typography>
                            {restaurant.restaurantNameEn && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                                {restaurant.restaurantNameEn}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                          {restaurant.contactName || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                          {restaurant.phone || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {restaurant.taxRegistrationNumber || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                        <StatusBadge status={restaurant.isActive !== false ? 'Active' : 'Inactive'} size="small" />
                      </TableCell>

                      <TableCell align="center" sx={{ textAlign: 'center !important', whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<StorefrontOutlined sx={{ fontSize: 13 }} />}
                            onClick={() => handleOpenBranchesModal(restaurant)}
                            sx={{ borderRadius: '6px', fontSize: '0.72rem', py: 0.3, px: 0.8 }}
                          >
                            {isRtl ? 'الفروع' : 'Branches'}
                          </Button>

                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<DescriptionOutlined sx={{ fontSize: 13 }} />}
                            onClick={() => handleOpenContractsModal(restaurant)}
                            sx={{
                              borderRadius: '6px',
                              fontSize: '0.72rem',
                              py: 0.3,
                              px: 0.9,
                              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                              color: '#ffffff',
                              fontWeight: 700,
                              boxShadow: 'none',
                              '&:hover': {
                                boxShadow: '0 2px 8px rgba(239, 108, 0, 0.3)',
                              }
                            }}
                          >
                            {isRtl ? 'العقود' : 'Contracts'}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<GroupOutlined sx={{ fontSize: 13 }} />}
                            onClick={() => handleOpenUsersModal(restaurant)}
                            sx={{ borderRadius: '6px', fontSize: '0.72rem', py: 0.3, px: 0.8 }}
                          >
                            {isRtl ? 'المستخدمين' : 'Users'}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Restaurant Modal */}
      <Dialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        maxWidth="md"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? 'إضافة وتسجيل مطعم شريك جديد' : 'Register New Partner Restaurant'}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          <Box component="form" noValidate dir={dir} style={{ direction: dir }} onSubmit={handleAddRestaurant} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
              {/* Row 1: Code & Arabic Name */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'كود المطعم (Restaurant Code)' : 'Restaurant Code'}
                  fullWidth
                  size="small"
                  value={newRestaurant.restaurantCode}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, restaurantCode: e.target.value })}
                  placeholder="e.g. REST-101"
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  label={isRtl ? 'اسم المطعم بالعربية *' : 'Restaurant Name (Arabic) *'}
                  fullWidth
                  size="small"
                  value={newRestaurant.restaurantNameAr}
                  onChange={(e) => {
                    setNewRestaurant({ ...newRestaurant, restaurantNameAr: e.target.value });
                    if (formErrors.restaurantNameAr) setFormErrors({ ...formErrors, restaurantNameAr: '' });
                  }}
                  error={Boolean(formErrors.restaurantNameAr)}
                  helperText={formErrors.restaurantNameAr}
                  required
                  placeholder={isRtl ? 'مثال: شاورما الريم' : 'e.g. Al-Reem Shawarma'}
                />
              </Grid>

              {/* Row 2: English Name & Legal Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'اسم المطعم بالإنجليزية' : 'Restaurant Name (English)'}
                  fullWidth
                  size="small"
                  value={newRestaurant.restaurantNameEn}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, restaurantNameEn: e.target.value })}
                  placeholder="e.g. Al-Reem Shawarma"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'الاسم التجاري / القانوني' : 'Legal Entity Name'}
                  fullWidth
                  size="small"
                  value={newRestaurant.legalName}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, legalName: e.target.value })}
                  placeholder={isRtl ? 'مثال: شركة الريم للأغذية ذ.م.م' : 'e.g. Al-Reem Foods LLC'}
                />
              </Grid>

              {/* Row 3: Contact Person & Phone */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'اسم المسؤول / جهة الاتصال' : 'Contact Person'}
                  fullWidth
                  size="small"
                  value={newRestaurant.contactName}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, contactName: e.target.value })}
                  placeholder={isRtl ? 'مثال: أ. محمد أحمد' : 'e.g. Mohamed Ahmed'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'رقم الهاتف للتواصل *' : 'Contact Phone *'}
                  fullWidth
                  size="small"
                  value={newRestaurant.phone}
                  onChange={(e) => {
                    setNewRestaurant({ ...newRestaurant, phone: e.target.value });
                    if (formErrors.phone) setFormErrors({ ...formErrors, phone: '' });
                  }}
                  error={Boolean(formErrors.phone)}
                  helperText={formErrors.phone}
                  required
                  placeholder="01xxxxxxxxx"
                  InputProps={{
                    startAdornment: <PhoneOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              {/* Row 4: Email & Tax Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'البريد الإلكتروني' : 'Email Address'}
                  fullWidth
                  size="small"
                  value={newRestaurant.email}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, email: e.target.value })}
                  placeholder="restaurant@example.com"
                  InputProps={{
                    startAdornment: <EmailOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'رقم التسجيل الضريبي' : 'Tax Registration Number'}
                  fullWidth
                  size="small"
                  value={newRestaurant.taxRegistrationNumber}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, taxRegistrationNumber: e.target.value })}
                  placeholder="e.g. 123-456-789"
                />
              </Grid>

              {/* Row 5: Active Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newRestaurant.isActive}
                      onChange={(e) => setNewRestaurant({ ...newRestaurant, isActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {isRtl ? 'المطعم نشط ومفعل لاستقبال وتوصيل الطلبات' : 'Restaurant is active for orders dispatch'}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>

            <DialogActions sx={{ px: 0, pt: 1.5, direction: dir }}>
              <Button onClick={() => setAddModalOpen(false)} color="inherit" size="small">
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={submittingRestaurant}
                startIcon={submittingRestaurant ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                sx={{
                  background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                  fontWeight: 700,
                  px: 2.5,
                }}
              >
                {submittingRestaurant ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ وإضافة المطعم' : 'Create Restaurant')}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ======================================================== */}
      {/* Restaurant Branches Management Modal                     */}
      {/* ======================================================== */}
      <Dialog
        open={branchesModalOpen}
        onClose={() => setBranchesModalOpen(false)}
        maxWidth="md"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? `إدارة وتحديد فروع مطعم: ${selectedRestaurantForBranches?.restaurantNameAr || selectedRestaurantForBranches?.name || ''}` : `Manage Branches for ${selectedRestaurantForBranches?.restaurantNameAr || selectedRestaurantForBranches?.name || ''}`}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          {/* Add / Edit Branch Form */}
          <Box component="form" onSubmit={handleSaveBranch} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: '10px', mb: 2.5, textAlign: isRtl ? 'right' : 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddLocationAltOutlined sx={{ fontSize: 18 }} />
              {editingBranch ? (isRtl ? 'تعديل بيانات الفرع' : 'Edit Branch') : (isRtl ? 'إضافة فرع وتحديد موقعه الجغرافي' : 'Add Branch & Set Location')}
            </Typography>
            <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'كود الفرع *' : 'Branch Code *'}
                  size="small"
                  fullWidth
                  value={branchForm.branchCode}
                  onChange={(e) => setBranchForm({ ...branchForm, branchCode: e.target.value })}
                  placeholder="BR-101"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  label={isRtl ? 'اسم الفرع بالعربية *' : 'Branch Name (Ar) *'}
                  size="small"
                  fullWidth
                  value={branchForm.branchNameAr}
                  onChange={(e) => setBranchForm({ ...branchForm, branchNameAr: e.target.value })}
                  placeholder={isRtl ? 'مثال: فرع الدقي' : 'e.g. Dokki Branch'}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'هاتف الفرع' : 'Branch Phone'}
                  size="small"
                  fullWidth
                  value={branchForm.phone}
                  onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                  placeholder="+201001234567"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'العنوان التفصيلي' : 'Address Text'}
                  size="small"
                  fullWidth
                  value={branchForm.addressText}
                  onChange={(e) => setBranchForm({ ...branchForm, addressText: e.target.value })}
                  placeholder={isRtl ? 'مثال: 15 شارع التحرير، الدقي' : 'e.g. 15 Tahrir St, Dokki'}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'خط العرض (Latitude) *' : 'Latitude *'}
                  size="small"
                  type="number"
                  fullWidth
                  value={branchForm.latitude}
                  onChange={(e) => setBranchForm({ ...branchForm, latitude: parseFloat(e.target.value) || 0 })}
                  placeholder="30.0444"
                  helperText={isRtl ? 'لتحديد أقرب المناديب' : 'For dispatching'}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'خط الطول (Longitude) *' : 'Longitude *'}
                  size="small"
                  type="number"
                  fullWidth
                  value={branchForm.longitude}
                  onChange={(e) => setBranchForm({ ...branchForm, longitude: parseFloat(e.target.value) || 0 })}
                  placeholder="31.2357"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={branchForm.isBatchingEnabled}
                      onChange={(e) => setBranchForm({ ...branchForm, isBatchingEnabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {isRtl ? 'تفعيل تجميع الطلبات (Batching)' : 'Enable Batching'}
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
                {editingBranch && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      setEditingBranch(null);
                      setBranchForm({
                        branchCode: `BR-${Math.floor(100 + Math.random() * 900)}`,
                        branchNameAr: '',
                        branchNameEn: '',
                        latitude: 30.0444,
                        longitude: 31.2357,
                        addressText: '',
                        phone: '',
                        maximumBatchOrders: 3,
                        isBatchingEnabled: true,
                        isActive: true,
                      });
                    }}
                  >
                    {t('cancel')}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={submittingBranch}
                  startIcon={submittingBranch ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                  sx={{
                    borderRadius: '8px',
                    fontWeight: 700,
                    px: 2.5,
                    bgcolor: 'secondary.main',
                    '&:hover': { bgcolor: 'secondary.dark' },
                  }}
                >
                  {submittingBranch ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : editingBranch ? (isRtl ? 'تحديث الفرع' : 'Update Branch') : (isRtl ? 'حفظ وإضافة الفرع' : 'Add Branch')}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Branches List Table */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, textAlign: isRtl ? 'right' : 'left' }}>
            {isRtl ? `قائمة فروع المطعم المسجلة (${restaurantBranches.length})` : `Registered Branches (${restaurantBranches.length})`}
          </Typography>
          {branchesLoading ? (
            <LoadingState />
          ) : restaurantBranches.length === 0 ? (
            <EmptyState message={isRtl ? 'لا توجد فروع مسجلة لهذا المطعم حتى الآن' : 'No branches registered for this restaurant yet'} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'كود الفرع' : 'Code'}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'اسم الفرع' : 'Branch Name'}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'الإحداثيات الجغرافية (Lat, Lng)' : 'Coordinates'}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'العنوان / الهاتف' : 'Address / Phone'}</TableCell>
                    <TableCell align="center" sx={{ textAlign: 'center !important' }}>{t('status')}</TableCell>
                    <TableCell align="center" sx={{ textAlign: 'center !important' }}>{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {restaurantBranches.map((branch, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                        {branch.branchCode || branch.code || '-'}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 600 }}>
                        {branch.branchNameAr || branch.nameAr || branch.name || '-'}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontSize: '0.78rem', color: 'primary.main', fontFamily: 'monospace' }}>
                        <LocationOnOutlined sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.3 }} />
                        {Number(branch.latitude || 0).toFixed(4)}, {Number(branch.longitude || 0).toFixed(4)}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontSize: '0.78rem' }}>
                        {branch.addressText || branch.phone || '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                        <Chip
                          label={branch.isActive !== false ? (isRtl ? 'نشط' : 'Active') : (isRtl ? 'معطل' : 'Inactive')}
                          color={branch.isActive !== false ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                        <IconButton size="small" color="primary" onClick={() => handleEditBranch(branch)}>
                          <EditOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, direction: dir }}>
          <Button onClick={() => setBranchesModalOpen(false)} color="inherit" size="small">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restaurant Portal Users Dialog (Full CRUD & Password & Status) */}
      <Dialog
        open={usersModalOpen}
        onClose={() => setUsersModalOpen(false)}
        maxWidth="md"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? `مستخدمو بوابة وتطبيق مطعم: ${selectedRestaurant?.restaurantNameAr || selectedRestaurant?.name || ''}` : `Portal Users for ${selectedRestaurant?.restaurantNameAr || selectedRestaurant?.name || ''}`}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          {/* Add User Form */}
          <Box component="form" onSubmit={handleAddRestaurantUser} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: '10px', mb: 2.5, textAlign: isRtl ? 'right' : 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              {isRtl ? 'إضافة وتسجيل مستخدم جديد لبوابة المطعم' : 'Add New Restaurant User'}
            </Typography>
            <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'اسم المستخدم (Login) *' : 'Username *'}
                  size="small"
                  fullWidth
                  value={newUserForm.userName}
                  onChange={(e) => setNewUserForm({ ...newUserForm, userName: e.target.value })}
                  placeholder="rest_user"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'الاسم الكامل / الظاهر' : 'Full Name'}
                  size="small"
                  fullWidth
                  value={newUserForm.fullName}
                  onChange={(e) => setNewUserForm({ ...newUserForm, fullName: e.target.value })}
                  placeholder={isRtl ? 'أحمد علي' : 'Ahmed Ali'}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'فرع المطعم' : 'Restaurant Branch'}
                  select
                  size="small"
                  fullWidth
                  value={newUserForm.restaurantBranchId}
                  onChange={(e) => setNewUserForm({ ...newUserForm, restaurantBranchId: e.target.value })}
                >
                  <MenuItem value="">{isRtl ? 'جميع الفروع / عام' : 'All Branches / General'}</MenuItem>
                  {userBranches.map((b) => {
                    const bId = b.restaurantBranchId || b.id || b.branchId;
                    return (
                      <MenuItem key={bId} value={bId}>
                        {b.branchNameAr || b.branchNameEn || b.branchCode || `Branch #${bId}`}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'الدور والصلاحية' : 'Role'}
                  select
                  size="small"
                  fullWidth
                  value={newUserForm.roleId || (restaurantRoles[0]?.roleId ?? '')}
                  onChange={(e) => setNewUserForm({ ...newUserForm, roleId: Number(e.target.value) })}
                  disabled={rolesLoading}
                  helperText={rolesLoading ? (isRtl ? 'جاري جلب الأدوار...' : 'Loading roles...') : ''}
                >
                  {restaurantRoles.length > 0 ? (
                    restaurantRoles.map((r) => {
                      const rId = r.roleId ?? r.id;
                      const label = isRtl
                        ? (r.roleNameAr || r.roleName || r.roleCode || `دور #${rId}`)
                        : (r.roleNameEn || r.roleName || r.roleCode || `Role #${rId}`);
                      return (
                        <MenuItem key={rId} value={rId}>
                          {label} {r.roleCode ? `(${r.roleCode})` : ''}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem value={newUserForm.roleId || 1}>
                      {rolesLoading ? (isRtl ? 'جاري التحميل...' : 'Loading...') : (isRtl ? 'مدير المطعم' : 'Restaurant Manager')}
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'رقم الموبايل' : 'Mobile Number'}
                  size="small"
                  fullWidth
                  value={newUserForm.mobile}
                  onChange={(e) => setNewUserForm({ ...newUserForm, mobile: e.target.value })}
                  placeholder="01xxxxxxxxx"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'البريد الإلكتروني' : 'Email'}
                  size="small"
                  fullWidth
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  placeholder="user@restaurant.com"
                />
              </Grid>
              <Grid item xs={12} sm={2.5}>
                <TextField
                  label={t('login.passwordLabel') + ' *'}
                  type="password"
                  size="small"
                  fullWidth
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="small"
                  disabled={submittingUser}
                  startIcon={submittingUser ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <PersonAddAlt1Outlined />}
                  sx={{
                    height: 40,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                  }}
                >
                  {submittingUser ? (isRtl ? 'جاري الإضافة...' : 'Adding...') : t('add')}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Users Table */}
          {usersLoading ? (
            <LoadingState />
          ) : restaurantUsers.length === 0 ? (
            <EmptyState message={t('noData')} subtitle={isRtl ? 'أضف مستخدم المطعم الأول من النموذج أعلاه' : 'Add the first user above'} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'اسم المستخدم' : 'Username'}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'الاسم الكامل' : 'Full Name'}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'الفرع' : 'Branch'}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('phone')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'الدور' : 'Role'}</TableCell>
                    <TableCell align="center" sx={{ textAlign: 'center !important' }}>{t('status')}</TableCell>
                    <TableCell align="center" sx={{ textAlign: 'center !important' }}>{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {restaurantUsers.map((u) => {
                    const matchedBranch = userBranches.find((b) => (b.restaurantBranchId || b.id || b.branchId) === u.restaurantBranchId);
                    return (
                      <TableRow key={u.id} hover>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                          @{u.userName}
                        </TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                          {u.fullName || u.displayName || '-'}
                        </TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                          {u.restaurantBranchId && u.restaurantBranchId !== 0 ? (
                            <Chip
                              label={matchedBranch ? (matchedBranch.branchNameAr || matchedBranch.branchNameEn || matchedBranch.branchCode) : `${isRtl ? 'فرع' : 'Branch'} #${u.restaurantBranchId}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem' }}
                            />
                          ) : (
                            <Chip
                              label={isRtl ? 'جميع الفروع' : 'All Branches'}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontSize: '0.75rem' }}>
                          {u.phone || u.mobile || u.email || '-'}
                        </TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                          <Chip label={u.role || 'Manager'} size="small" sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                          <Tooltip title={isRtl ? 'انقر لتبديل حالة التفعيل' : 'Click to toggle active status'}>
                            <Chip
                              label={u.isActive !== false ? t('active') : t('inactive')}
                              color={u.isActive !== false ? 'success' : 'default'}
                              size="small"
                              onClick={() => handleToggleUserStatus(u)}
                              sx={{ cursor: 'pointer', fontWeight: 700 }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={{ textAlign: 'center !important', whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title={isRtl ? 'تعديل البيانات' : 'Edit User'}>
                              <IconButton size="small" color="primary" onClick={() => handleOpenEditUser(u)}>
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isRtl ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}>
                              <IconButton size="small" color="warning" onClick={() => handleOpenResetPassword(u)}>
                                <KeyOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, direction: dir }}>
          <Button onClick={() => setUsersModalOpen(false)} color="inherit" size="small">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPwdOpen}
        onClose={() => setResetPwdOpen(false)}
        maxWidth="xs"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? `إعادة تعيين كلمة المرور: @${selectedUserForPwd?.userName}` : `Reset Password for @${selectedUserForPwd?.userName}`}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          <Box sx={{ mt: 1 }}>
            <TextField
              label={isRtl ? 'كلمة المرور الجديدة' : 'New Password'}
              type="password"
              fullWidth
              size="small"
              value={newPasswordValue}
              onChange={(e) => setNewPasswordValue(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, direction: dir }}>
          <Button onClick={() => setResetPwdOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={submittingPwd}
            startIcon={submittingPwd ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            onClick={handleSaveResetPassword}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {submittingPwd ? (isRtl ? 'جاري التحديث...' : 'Updating...') : (isRtl ? 'تحديث كلمة المرور' : 'Update Password')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editUserOpen}
        onClose={() => setEditUserOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? `تعديل بيانات المستخدم: @${editUserForm.userName}` : `Edit User: @${editUserForm.userName}`}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <TextField
              label={isRtl ? 'الاسم الكامل' : 'Full Name'}
              fullWidth
              size="small"
              value={editUserForm.fullName}
              onChange={(e) => setEditUserForm({ ...editUserForm, fullName: e.target.value })}
            />
            <TextField
              label={isRtl ? 'فرع المطعم' : 'Restaurant Branch'}
              select
              fullWidth
              size="small"
              value={editUserForm.restaurantBranchId}
              onChange={(e) => setEditUserForm({ ...editUserForm, restaurantBranchId: e.target.value })}
            >
              <MenuItem value="">{isRtl ? 'جميع الفروع / عام' : 'All Branches / General'}</MenuItem>
              {userBranches.map((b) => {
                const bId = b.restaurantBranchId || b.id || b.branchId;
                return (
                  <MenuItem key={bId} value={bId}>
                    {b.branchNameAr || b.branchNameEn || b.branchCode || `Branch #${bId}`}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              label={t('phone')}
              fullWidth
              size="small"
              value={editUserForm.phone}
              onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })}
            />
            <TextField
              label={isRtl ? 'البريد الإلكتروني' : 'Email'}
              fullWidth
              size="small"
              value={editUserForm.email}
              onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
            />
            <TextField
              label={isRtl ? 'الدور والصلاحية' : 'Role'}
              select
              fullWidth
              size="small"
              value={editUserForm.role}
              onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
            >
              <MenuItem value="Manager">{isRtl ? 'مدير المطعم (Manager)' : 'Manager'}</MenuItem>
              <MenuItem value="Cashier">{isRtl ? 'كاشير / محاسب (Cashier)' : 'Cashier'}</MenuItem>
              <MenuItem value="Operator">{isRtl ? 'مشغل طلبات (Operator)' : 'Operator'}</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, direction: dir }}>
          <Button onClick={() => setEditUserOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={submittingEditUser}
            startIcon={submittingEditUser ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            onClick={handleSaveEditUser}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {submittingEditUser ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* Restaurant Contracts Management Modal (POST /contracts)  */}
      {/* ======================================================== */}
      <Dialog
        open={contractsModalOpen}
        onClose={() => setContractsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', width: 36, height: 36 }}>
                <DescriptionOutlined fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {isRtl ? 'إدارة وتوثيق عقود المطعم' : 'Restaurant Contracts Management'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedRestaurantForContracts?.restaurantNameAr || selectedRestaurantForContracts?.name || ''}
                  {selectedRestaurantForContracts?.restaurantCode && (
                    <Chip
                      label={selectedRestaurantForContracts.restaurantCode}
                      size="small"
                      sx={{ ml: 1, mr: 1, height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                    />
                  )}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${isRtl ? 'إجمالي العقود' : 'Total Contracts'}: ${restaurantContracts.length}`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          </Box>
        </DialogTitle>

        <DialogContent dir={dir} style={{ direction: dir }}>
          {/* Section 1: Add New Contract Form */}
          <Box
            component="form"
            onSubmit={handleSaveContract}
            sx={{
              p: 2.5,
              bgcolor: 'action.hover',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              mb: 3,
              textAlign: isRtl ? 'right' : 'left',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddCircleOutline sx={{ fontSize: 18 }} />
              {isRtl ? 'إبرام وتوثيق عقد تجاري جديد (POST /api/v1/restaurants/contracts)' : 'Create New Commercial Contract'}
            </Typography>

            <Grid container spacing={2} dir={dir} style={{ direction: dir }}>
              {/* Contract Number */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'رقم العقد التوثيقي *' : 'Contract Number *'}
                  size="small"
                  fullWidth
                  value={contractForm.contractNumber}
                  onChange={(e) => setContractForm({ ...contractForm, contractNumber: e.target.value })}
                  placeholder="CNT-101-2026-001"
                  required
                />
              </Grid>

              {/* Effective From */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'تاريخ بداية السريان *' : 'Effective From *'}
                  type="date"
                  size="small"
                  fullWidth
                  value={contractForm.effectiveFrom}
                  onChange={(e) => setContractForm({ ...contractForm, effectiveFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              {/* Effective To */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'تاريخ نهاية السريان *' : 'Effective To *'}
                  type="date"
                  size="small"
                  fullWidth
                  value={contractForm.effectiveTo}
                  onChange={(e) => setContractForm({ ...contractForm, effectiveTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              {/* Additional Fee Percentage */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'النسبة الإضافية على الطلب (%)' : 'Additional Order Fee (%)'}
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  value={contractForm.additionalOrderFeePercentage}
                  onChange={(e) => setContractForm({ ...contractForm, additionalOrderFeePercentage: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Statement Cycle */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'دورة كشف الحساب' : 'Statement Cycle'}
                  select
                  size="small"
                  fullWidth
                  value={contractForm.statementCycle}
                  onChange={(e) => setContractForm({ ...contractForm, statementCycle: Number(e.target.value) })}
                >
                  <MenuItem value={0}>{isRtl ? 'يومي (Daily)' : 'Daily'}</MenuItem>
                  <MenuItem value={1}>{isRtl ? 'أسبوعي (Weekly)' : 'Weekly'}</MenuItem>
                  <MenuItem value={2}>{isRtl ? 'نصف شهري (Bi-Weekly)' : 'Bi-Weekly'}</MenuItem>
                  <MenuItem value={3}>{isRtl ? 'شهري (Monthly)' : 'Monthly'}</MenuItem>
                </TextField>
              </Grid>

              {/* Payment Term Days */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'مهلة السداد (بالأيام)' : 'Payment Term Days'}
                  select
                  size="small"
                  fullWidth
                  value={contractForm.paymentTermDays}
                  onChange={(e) => setContractForm({ ...contractForm, paymentTermDays: Number(e.target.value) })}
                >
                  <MenuItem value={0}>{isRtl ? 'فوري عند الإصدار (0 يوم)' : 'Immediate (0 Days)'}</MenuItem>
                  <MenuItem value={3}>{isRtl ? '3 أيام' : '3 Days'}</MenuItem>
                  <MenuItem value={7}>{isRtl ? '7 أيام (أسبوع)' : '7 Days'}</MenuItem>
                  <MenuItem value={14}>{isRtl ? '14 يوماً (أسبوعين)' : '14 Days'}</MenuItem>
                  <MenuItem value={30}>{isRtl ? '30 يوماً (شهر)' : '30 Days'}</MenuItem>
                </TextField>
              </Grid>

              {/* Courier Payment Display Mode */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label={isRtl ? 'عرض حساب المندوب' : 'Courier Display Mode'}
                  select
                  size="small"
                  fullWidth
                  value={contractForm.courierPaymentDisplayMode}
                  onChange={(e) => setContractForm({ ...contractForm, courierPaymentDisplayMode: Number(e.target.value) })}
                >
                  <MenuItem value={0}>{isRtl ? 'إخفاء الحساب (0 - Default)' : 'Hide Account (0)'}</MenuItem>
                  <MenuItem value={1}>{isRtl ? 'إظهار حساب المندوب (1)' : 'Show Account (1)'}</MenuItem>
                </TextField>
              </Grid>

              {/* Status Switch */}
              <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={contractForm.status === 2}
                      onChange={(e) => setContractForm({ ...contractForm, status: e.target.checked ? 2 : 1 })}
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {isRtl ? 'الحالة:' : 'Status:'}
                      </Typography>
                      <Chip
                        label={contractForm.status === 2 ? (isRtl ? 'نشط (2)' : 'Active (2)') : (isRtl ? 'معطل (1)' : 'Disabled (1)')}
                        color={contractForm.status === 2 ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 700, height: 22, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
              </Grid>

              {/* Allow Zero Delivery Fee */}
              <Grid item xs={12} sm={9} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={contractForm.allowZeroDeliveryFee}
                      onChange={(e) => setContractForm({ ...contractForm, allowZeroDeliveryFee: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {isRtl ? 'السماح بطلبات توصيل مجانية بدون رسوم توصيل (Allow Zero Delivery Fee)' : 'Allow zero delivery fee orders'}
                    </Typography>
                  }
                />
              </Grid>

              {/* Branch Selection (branchIds) */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {isRtl ? 'الفروع المشمولة بهذا العقد (اختياري / افتراضياً الكل):' : 'Covered Branches for this contract:'}
                  </Typography>
                  {contractBranches.length > 0 && (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', py: 0 }}
                      onClick={() => {
                        if (contractForm.branchIds.length === contractBranches.length) {
                          setContractForm({ ...contractForm, branchIds: [] });
                        } else {
                          setContractForm({
                            ...contractForm,
                            branchIds: contractBranches.map((b) => Number(b.restaurantBranchId || b.id)),
                          });
                        }
                      }}
                    >
                      {contractForm.branchIds.length === contractBranches.length
                        ? (isRtl ? 'إلغاء تحديد الكل' : 'Deselect All')
                        : (isRtl ? 'تحديد جميع الفروع' : 'Select All Branches')}
                    </Button>
                  )}
                </Box>

                {contractBranches.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {isRtl ? 'لا توجد فروع مسجلة لهذا المطعم حالياً. سيتم ربط العقد بالمركز الرئيسي.' : 'No branches found for this restaurant yet. Contract applies generally.'}
                  </Typography>
                ) : (
                  <FormGroup row sx={{ gap: 1 }}>
                    {contractBranches.map((branch) => {
                      const branchId = Number(branch.restaurantBranchId || branch.id);
                      const isSelected = contractForm.branchIds.includes(branchId);
                      return (
                        <FormControlLabel
                          key={branchId}
                          control={
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setContractForm({ ...contractForm, branchIds: [...contractForm.branchIds, branchId] });
                                } else {
                                  setContractForm({ ...contractForm, branchIds: contractForm.branchIds.filter((id) => id !== branchId) });
                                }
                              }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontWeight: isSelected ? 700 : 500 }}>
                              {branch.branchNameAr || branch.branchNameEn || branch.branchCode} ({branch.branchCode || `#${branchId}`})
                            </Typography>
                          }
                        />
                      );
                    })}
                  </FormGroup>
                )}
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={submittingContract}
                  startIcon={submittingContract ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AddCircleOutline />}
                  sx={{
                    background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                    fontWeight: 700,
                    px: 3,
                    py: 0.8,
                  }}
                >
                  {submittingContract ? (isRtl ? 'جاري توثيق العقد...' : 'Saving Contract...') : (isRtl ? 'حفظ وتوثيق العقد' : 'Create & Save Contract')}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Section 2: Registered Contracts List */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptLongOutlined sx={{ fontSize: 18 }} />
              {isRtl ? 'سجل العقود الموثقة والمسجلة للمطعم' : 'Registered Contracts History'}
            </Typography>

            {contractsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : restaurantContracts.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover', borderRadius: '8px' }}>
                <Typography variant="body2" color="text.secondary">
                  {isRtl ? 'لا توجد عقود مسجلة لهذا المطعم حتى الآن. يمكنك إبرام عقد جديد من النموذج أعلاه.' : 'No contracts recorded for this restaurant yet. Create one above.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700, textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'رقم العقد' : 'Contract #'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'فترة السريان' : 'Effective Period'}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center !important' }}>{isRtl ? 'النسبة الإضافية' : 'Fee %'}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center !important' }}>{isRtl ? 'دورة الفوترة' : 'Cycle'}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center !important' }}>{isRtl ? 'مهلة السداد' : 'Payment Term'}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center !important' }}>{isRtl ? 'التوصيل المجاني' : 'Zero Fee'}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center !important' }}>{t('status')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center !important', width: 90 }}>{isRtl ? 'الإجراءات' : 'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {restaurantContracts.map((contract, idx) => {
                      const contractId = contract.restaurantContractId || contract.id || contract.contractId || idx;
                      const cycleLabels = {
                        0: isRtl ? 'يومي' : 'Daily',
                        1: isRtl ? 'أسبوعي' : 'Weekly',
                        2: isRtl ? 'نصف شهري' : 'Bi-Weekly',
                        3: isRtl ? 'شهري' : 'Monthly',
                      };
                      const isActiveContract = contract.status === 2 || contract.status === 'Active';
                      return (
                        <TableRow key={contractId} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DescriptionOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
                              {contract.contractNumber || `CNT-${contractId}`}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                              {contract.effectiveFrom ? new Date(contract.effectiveFrom).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US') : '-'}
                              {' → '}
                              {contract.effectiveTo ? new Date(contract.effectiveTo).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={`${contract.additionalOrderFeePercentage || 0}%`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 700, height: 22 }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {cycleLabels[contract.statementCycle] || contract.statementCycle || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {contract.paymentTermDays !== undefined ? `${contract.paymentTermDays} ${isRtl ? 'يوم' : 'days'}` : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={contract.allowZeroDeliveryFee ? (isRtl ? 'مسموح' : 'Allowed') : (isRtl ? 'غير مسموح' : 'Not Allowed')}
                              size="small"
                              color={contract.allowZeroDeliveryFee ? 'success' : 'default'}
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={isActiveContract ? (isRtl ? 'نشط وساري (2)' : 'Active (2)') : (isRtl ? 'معطل / مسودة (1)' : 'Inactive (1)')}
                              color={isActiveContract ? 'success' : 'default'}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                bgcolor: isActiveContract ? undefined : 'action.selected',
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Tooltip title={isRtl ? 'تعديل بيانات العقد والحالة' : 'Edit Contract & Status'}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEditContract(contract)}
                                sx={{
                                  bgcolor: 'action.hover',
                                  '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
                                }}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 1.5, direction: dir }}>
          <Button onClick={() => setContractsModalOpen(false)} color="inherit" size="small">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* Edit Contract Dialog (PUT /api/v1/restaurants/contracts) */}
      {/* ======================================================== */}
      <Dialog
        open={editContractOpen}
        onClose={() => setEditContractOpen(false)}
        maxWidth="md"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', color: '#FFFFFF', width: 34, height: 34 }}>
              <EditOutlined fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {isRtl ? 'تعديل بيانات العقد التجاري' : 'Edit Commercial Contract'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editContractForm.contractNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dir={dir} style={{ direction: dir }}>
          <Box component="form" onSubmit={handleSaveEditContract} sx={{ mt: 1 }}>
            <Grid container spacing={2} dir={dir} style={{ direction: dir }}>
              {/* Contract Number */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'رقم العقد *' : 'Contract Number *'}
                  size="small"
                  fullWidth
                  value={editContractForm.contractNumber}
                  onChange={(e) => setEditContractForm({ ...editContractForm, contractNumber: e.target.value })}
                  required
                />
              </Grid>

              {/* Effective From */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'تاريخ بداية السريان *' : 'Effective From *'}
                  type="date"
                  size="small"
                  fullWidth
                  value={editContractForm.effectiveFrom}
                  onChange={(e) => setEditContractForm({ ...editContractForm, effectiveFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              {/* Effective To */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'تاريخ نهاية السريان *' : 'Effective To *'}
                  type="date"
                  size="small"
                  fullWidth
                  value={editContractForm.effectiveTo}
                  onChange={(e) => setEditContractForm({ ...editContractForm, effectiveTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              {/* Additional Fee Percentage */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'النسبة الإضافية على الطلب (%)' : 'Additional Order Fee (%)'}
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  value={editContractForm.additionalOrderFeePercentage}
                  onChange={(e) => setEditContractForm({ ...editContractForm, additionalOrderFeePercentage: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Statement Cycle */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'دورة كشف الحساب' : 'Statement Cycle'}
                  select
                  size="small"
                  fullWidth
                  value={editContractForm.statementCycle}
                  onChange={(e) => setEditContractForm({ ...editContractForm, statementCycle: Number(e.target.value) })}
                >
                  <MenuItem value={0}>{isRtl ? 'يومي (Daily)' : 'Daily'}</MenuItem>
                  <MenuItem value={1}>{isRtl ? 'أسبوعي (Weekly)' : 'Weekly'}</MenuItem>
                  <MenuItem value={2}>{isRtl ? 'نصف شهري (Bi-Weekly)' : 'Bi-Weekly'}</MenuItem>
                  <MenuItem value={3}>{isRtl ? 'شهري (Monthly)' : 'Monthly'}</MenuItem>
                </TextField>
              </Grid>

              {/* Payment Term Days */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'مهلة السداد (بالأيام)' : 'Payment Term Days'}
                  select
                  size="small"
                  fullWidth
                  value={editContractForm.paymentTermDays}
                  onChange={(e) => setEditContractForm({ ...editContractForm, paymentTermDays: Number(e.target.value) })}
                >
                  <MenuItem value={0}>{isRtl ? 'فوري عند الإصدار (0 يوم)' : 'Immediate (0 Days)'}</MenuItem>
                  <MenuItem value={3}>{isRtl ? '3 أيام' : '3 Days'}</MenuItem>
                  <MenuItem value={7}>{isRtl ? '7 أيام (أسبوع)' : '7 Days'}</MenuItem>
                  <MenuItem value={14}>{isRtl ? '14 يوماً (أسبوعين)' : '14 Days'}</MenuItem>
                  <MenuItem value={30}>{isRtl ? '30 يوماً (شهر)' : '30 Days'}</MenuItem>
                </TextField>
              </Grid>

              {/* Courier Payment Display Mode */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'عرض حساب المندوب' : 'Courier Display Mode'}
                  select
                  size="small"
                  fullWidth
                  value={editContractForm.courierPaymentDisplayMode}
                  onChange={(e) => setEditContractForm({ ...editContractForm, courierPaymentDisplayMode: Number(e.target.value) })}
                >
                  <MenuItem value={0}>{isRtl ? 'إخفاء الحساب (0 - Default)' : 'Hide Account (0)'}</MenuItem>
                  <MenuItem value={1}>{isRtl ? 'إظهار حساب المندوب (1)' : 'Show Account (1)'}</MenuItem>
                </TextField>
              </Grid>

              {/* Status Switch (1 = Disabled, 2 = Active) */}
              <Grid item xs={12} sm={8}>
                <Box
                  sx={{
                    p: 1.2,
                    border: '1px solid',
                    borderColor: editContractForm.status === 2 ? 'success.main' : 'divider',
                    borderRadius: '8px',
                    bgcolor: editContractForm.status === 2 ? 'success.lighter' : 'action.hover',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editContractForm.status === 2}
                        onChange={(e) => setEditContractForm({ ...editContractForm, status: e.target.checked ? 2 : 1 })}
                        color="success"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {isRtl ? 'حالة سريان العقد:' : 'Contract Status:'}
                        </Typography>
                        <Chip
                          label={
                            editContractForm.status === 2
                              ? (isRtl ? 'مفعل ونشط (الحالة: 2)' : 'Active (Status: 2)')
                              : (isRtl ? 'معطل وغير مفعل (الحالة: 1)' : 'Disabled / Inactive (Status: 1)')
                          }
                          color={editContractForm.status === 2 ? 'success' : 'default'}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3 }}>
                    {editContractForm.status === 2
                      ? (isRtl ? 'العقد ساري ومفعل (Status = 2)' : 'Contract is actively in effect (Status = 2)')
                      : (isRtl ? 'العقد معطل وغير ساري (Status = 1)' : 'Contract is disabled (Status = 1)')}
                  </Typography>
                </Box>
              </Grid>

              {/* Allow Zero Delivery Fee */}
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editContractForm.allowZeroDeliveryFee}
                      onChange={(e) => setEditContractForm({ ...editContractForm, allowZeroDeliveryFee: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {isRtl ? 'السماح بطلبات توصيل مجانية بدون رسوم توصيل (Allow Zero Delivery Fee)' : 'Allow zero delivery fee orders'}
                    </Typography>
                  }
                />
              </Grid>

              {/* Branch Selection (branchIds) */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {isRtl ? 'الفروع المشمولة بهذا العقد (اختياري / افتراضياً الكل):' : 'Covered Branches for this contract:'}
                  </Typography>
                  {contractBranches.length > 0 && (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', py: 0 }}
                      onClick={() => {
                        if (editContractForm.branchIds.length === contractBranches.length) {
                          setEditContractForm({ ...editContractForm, branchIds: [] });
                        } else {
                          setEditContractForm({
                            ...editContractForm,
                            branchIds: contractBranches.map((b) => Number(b.restaurantBranchId || b.id)),
                          });
                        }
                      }}
                    >
                      {editContractForm.branchIds.length === contractBranches.length
                        ? (isRtl ? 'إلغاء تحديد الكل' : 'Deselect All')
                        : (isRtl ? 'تحديد جميع الفروع' : 'Select All Branches')}
                    </Button>
                  )}
                </Box>

                {contractBranches.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {isRtl ? 'لا توجد فروع مسجلة لهذا المطعم حالياً.' : 'No branches found for this restaurant.'}
                  </Typography>
                ) : (
                  <FormGroup row sx={{ gap: 1 }}>
                    {contractBranches.map((branch) => {
                      const branchId = Number(branch.restaurantBranchId || branch.id);
                      const isSelected = editContractForm.branchIds.includes(branchId);
                      return (
                        <FormControlLabel
                          key={branchId}
                          control={
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditContractForm({ ...editContractForm, branchIds: [...editContractForm.branchIds, branchId] });
                                } else {
                                  setEditContractForm({ ...editContractForm, branchIds: editContractForm.branchIds.filter((id) => id !== branchId) });
                                }
                              }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontWeight: isSelected ? 700 : 500 }}>
                              {branch.branchNameAr || branch.branchNameEn || branch.branchCode} ({branch.branchCode || `#${branchId}`})
                            </Typography>
                          }
                        />
                      );
                    })}
                  </FormGroup>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 1.5, direction: dir }}>
          <Button onClick={() => setEditContractOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={submittingEditContract}
            startIcon={submittingEditContract ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            onClick={handleSaveEditContract}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {submittingEditContract ? (isRtl ? 'جاري حفظ التعديل...' : 'Saving...') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: '8px', fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Restaurants;
